import { isPlatformBrowser } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@freelancer/activated-route';
import { Auth } from '@freelancer/auth';
import type { DatastoreCollection } from '@freelancer/datastore';
import { Datastore } from '@freelancer/datastore';
import type {
  Currency,
  ReferralTokenCollection,
  User,
  UsersCollection,
} from '@freelancer/datastore/collections';
import { GiveGetService } from '@freelancer/give-get';
import { LocalStorage } from '@freelancer/local-storage';
import { FreelancerCurrencyPipe } from '@freelancer/pipes';
import { REFERRER_QUERY_PARAM } from '@freelancer/share';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { ContainerSize } from '@freelancer/ui/container';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { LinkColor, LinkUnderline } from '@freelancer/ui/link';
import { ModalService, ModalSize } from '@freelancer/ui/modal';
import { SpinnerSize, SpinnerType } from '@freelancer/ui/spinner';
import { FontColor, FontWeight, TextSize } from '@freelancer/ui/text';
import { AvatarSize } from '@freelancer/ui/user-avatar';
import { isDefined } from '@freelancer/utils';
import { UntilDestroy } from '@ngneat/until-destroy';
import { LoginOrSignup } from 'app/login-signup/login-signup-modal';
import { CookieService } from 'ngx-cookie';
import { Subscription, combineLatest, map, of, type Observable } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

interface IconToText {
  icon: string;
  text: string;
}

@UntilDestroy()
@Component({
  selector: 'app-give-get-footer',
  template: `
    <div
      *ngIf="showGiveGetFooter$ | flAsync"
      class="GiveGetFooter"
    >
      <div
        flTrackingSection="GiveGetFooter"
        class="GiveGetFooter-background"
      >
        <fl-container
          class="GiveGetFooter-container"
          [size]="ContainerSize.DESKTOP_XLARGE"
        >
          <div class="GiveGetFooter-grid">
            <fl-user-avatar
              [users]="[user$ | flAsync]"
              [size]="AvatarSize.XLARGE"
              [flMarginRight]="'small'"
              [flShowDesktop]="true"
            ></fl-user-avatar>
            <div
              class="GiveGetFooter-textContainer"
              [flMarginRight]="'mid'"
            >
              <ng-container *ngIf="isReady$ | flAsync; else loadingState">
                <ng-container *ngIf="user$ | flAsync as user">
                  <ng-container *ngIf="bonusAmount$ | flAsync as bonusAmount">
                    <div class="GiveGetFooter-titleContainer">
                      <fl-user-avatar
                        [flMarginRight]="'small'"
                        [flMarginBottom]="'xsmall'"
                        [users]="[user]"
                        [size]="AvatarSize.MID"
                        [flHideDesktop]="true"
                      ></fl-user-avatar>
                      <fl-text
                        i18n="Give get footer header"
                        [color]="FontColor.LIGHT"
                        [weight]="FontWeight.BOLD"
                        [size]="TextSize.SMALL"
                        [sizeTablet]="TextSize.LARGE"
                        [flMarginBottomTablet]="'xxsmall'"
                      >
                        {{ user.displayName }} has given you
                        {{ bonusAmount }}
                        to try Freelancer
                      </fl-text>
                    </div>
                    <fl-text
                      i18n="Freelacner marketplace text"
                      [size]="TextSize.XSMALL"
                      [sizeTablet]="TextSize.SMALL"
                      [color]="FontColor.LIGHT"
                      [flMarginBottom]="'small'"
                      [flHideDesktop]="true"
                    >
                      Get started today and use your
                      {{ bonusAmount.split(' ')[0] }} to get anything (and we
                      mean anything) done, from growing your business to mowing
                      your lawn. Valid for new users only,
                      <fl-link
                        flTrackingLabel="GiveGetFooter-T&C"
                        [size]="TextSize.SMALL"
                        [color]="LinkColor.LIGHT"
                        [link]="'/get/' + user.username"
                        [underline]="LinkUnderline.ALWAYS"
                        [queryParams]="{
                          ref: 'social-referral-terms'
                        }"
                      >
                        T&Cs apply.
                      </fl-link>
                    </fl-text>

                    <fl-text
                      i18n="Freelacner marketplace text"
                      [size]="TextSize.XSMALL"
                      [sizeTablet]="TextSize.SMALL"
                      [color]="FontColor.LIGHT"
                      [flMarginBottom]="'small'"
                      [flShowDesktop]="true"
                    >
                      Freelancer is the world's largest services marketplace.
                      Get started today and use your
                      {{ bonusAmount.split(' ')[0] }} to get anything (and we
                      mean anything) done, from growing your business to mowing
                      your lawn. Valid for new users only,
                      <fl-link
                        flTrackingLabel="GiveGetFooter-T&C"
                        [size]="TextSize.SMALL"
                        [color]="LinkColor.LIGHT"
                        [link]="'/get/' + user.username"
                        [underline]="LinkUnderline.ALWAYS"
                        [queryParams]="{
                          ref: 'social-referral-terms'
                        }"
                      >
                        T&Cs apply.
                      </fl-link>
                    </fl-text>
                    <div>
                      <fl-button
                        i18n="Claim bonus amount"
                        flTrackingLabel="GiveGetFooter-ClaimBonus"
                        [color]="ButtonColor.PRIMARY"
                        [size]="ButtonSize.MID"
                        [sizeTablet]="ButtonSize.LARGE"
                        [flMarginRight]="'large'"
                        (click)="openLoginSignupModal()"
                      >
                        Claim my
                        {{ bonusAmount.split(' ')[0] }}
                      </fl-button>
                      <fl-link
                        i18n="What can user do with bonus amount"
                        flTrackingLabel="GiveGetFooter-WhatCanIDoWithIt"
                        [color]="LinkColor.PINK"
                        [link]="'/get/' + user.username"
                        [queryParams]="{
                          ref: 'social-referral-terms'
                        }"
                        [size]="TextSize.XSMALL"
                        [sizeTablet]="TextSize.MID"
                      >
                        What can I do with it?
                      </fl-link>
                    </div>
                  </ng-container>
                </ng-container>
              </ng-container>
            </div>
            <div class="GiveGetFooter-iconContainer">
              <div
                *ngFor="let item of listOfFeatures"
                class="GiveGetFooter-listOfFeatures"
                [flMarginBottom]="'xxsmall'"
              >
                <fl-icon
                  [color]="IconColor.LIGHT"
                  [name]="item.icon"
                  [flMarginRight]="'small'"
                >
                </fl-icon>
                <fl-text
                  [color]="FontColor.LIGHT"
                  [size]="TextSize.SMALL"
                >
                  {{ item.text }}
                </fl-text>
              </div>
            </div>
          </div>
        </fl-container>
        <fl-picture
          class="GiveGetFooter-image"
          [src]="'give-get-footer/freelancerBox.png'"
        ></fl-picture>
        <fl-icon
          class="GiveGetFooter-closeIcon"
          flTrackingLabel="GiveGetter-CloseIcon"
          [color]="IconColor.LIGHT"
          [name]="'ui-close'"
          [size]="IconSize.SMALL"
          [hoverAnimation]="'spin-and-fade'"
          (click)="closeFooter()"
        ></fl-icon>
      </div>
    </div>

    <ng-template #loadingState>
      <fl-spinner
        class="GiveGetFooter-loadingState"
        flTrackingLabel="GiveGetFooterLoading"
        [size]="SpinnerSize.LARGE"
      ></fl-spinner>
    </ng-template>
  `,
  styleUrls: ['./give-get-footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GiveGetFooterComponent implements OnInit, OnDestroy {
  AvatarSize = AvatarSize;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  ContainerSize = ContainerSize;
  FontColor = FontColor;
  FontWeight = FontWeight;
  IconColor = IconColor;
  IconSize = IconSize;
  LinkColor = LinkColor;
  LinkUnderline = LinkUnderline;
  LoginOrSignup = LoginOrSignup;
  ModalSize = ModalSize;
  SpinnerSize = SpinnerSize;
  SpinnerType = SpinnerType;
  TextSize = TextSize;

  user$: Observable<User>;
  bonusAmount$: Observable<string | null>;

  // Default is USD
  DEFAULT_CURRENCY_ID = 1;
  USD_AMOUNT_THRESHOLD = 1;
  DEFAULT_BONUS_AMOUNT = 20;
  loggedOutUserCurrency$: Observable<Currency>;

  listOfFeatures: readonly IconToText[];

  isReady$: Observable<boolean>;
  showGiveGetFooter$: Observable<boolean>;
  userCollection: DatastoreCollection<UsersCollection>;

  private subscriptions = new Subscription();

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private activatedRoute: ActivatedRoute,
    private auth: Auth,
    private cookies: CookieService,
    private currencyPipe: FreelancerCurrencyPipe,
    private datastore: Datastore,
    private giveGetService: GiveGetService,
    private localStorage: LocalStorage,
    private modalService: ModalService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.listOfFeatures = [
      {
        icon: 'ui-globe',
        text: $localize`World’s largest services marketplace`,
      },
      {
        icon: 'ui-suitcase-v2',
        text: $localize`Any job you can possibly think of`,
      },
      {
        icon: 'ui-dollar-alt',
        text: $localize`Save up to 90% & get quotes for free`,
      },
      {
        icon: 'ui-smile-outline',
        text: $localize`Pay only when you're 100% happy`,
      },
    ];
    if (isPlatformBrowser(this.platformId)) {
      const localUsername$ = this.localStorage.get('giveGetReferrerUsername');

      const latestQueryParamLocaStorage$ = combineLatest([
        this.activatedRoute.queryParams.pipe(
          map(queryParamMap => queryParamMap[REFERRER_QUERY_PARAM]),
        ),
        localUsername$,
      ]);

      const referrerUsername$ = latestQueryParamLocaStorage$.pipe(
        switchMap(async ([queryParamUserName, localUsername]) => {
          return queryParamUserName || localUsername;
        }),
        filter(isDefined),
      );

      const referralTokenCollection =
        this.datastore.collection<ReferralTokenCollection>(
          'referralToken',
          query =>
            query.search(
              referrerUsername$.pipe(
                map(username => ({
                  referrer_username: username,
                })),
              ),
            ),
        );

      const validUsername$ = combineLatest([
        referralTokenCollection.status$,
        referrerUsername$,
      ]).pipe(
        filter(
          ([status, _]) =>
            (isDefined(status.error) && !status.error) || status.ready,
        ),
        map(([_, username]) => username),
      );

      const userCollection = this.datastore.collection<UsersCollection>(
        'users',
        query => query.where('username', 'equalsIgnoreCase', validUsername$),
      );

      this.user$ = userCollection.valueChanges().pipe(
        map(users => users[0]),
        filter(isDefined),
      );

      const giveGetFooterClosed$ = this.localStorage.get('giveGetFooterClosed');

      this.showGiveGetFooter$ = combineLatest([
        validUsername$,
        giveGetFooterClosed$,
        this.auth.isLoggedIn(),
      ]).pipe(
        map(([username, footerClosed, isLoggedIn]) => {
          return !isLoggedIn && isDefined(username) && !footerClosed;
        }),
      );

      const currencyToUse$ = this.giveGetService.getCurrencyToUse();
      const bonusAmountValue$ =
        this.giveGetService.getBonusAmount(currencyToUse$);
      this.bonusAmount$ = combineLatest([
        currencyToUse$,
        bonusAmountValue$,
      ]).pipe(
        map(([currency, bonusAmount]) => {
          return this.currencyPipe.transform(
            bonusAmount,
            currency.code,
            false,
            false,
          );
        }),
      );

      this.isReady$ = combineLatest([this.user$, this.bonusAmount$]).pipe(
        map(([user, bonusAmount]) => isDefined(user) && isDefined(bonusAmount)),
      );
      this.subscriptions.add(
        this.user$.subscribe(user =>
          this.localStorage.set('giveGetReferrerUsername', user.username),
        ),
      );
      this.subscriptions.add(
        latestQueryParamLocaStorage$.subscribe(
          ([queryParam, giveGetReferrer]) => {
            if (queryParam && queryParam !== giveGetReferrer) {
              this.localStorage.set('giveGetFooterClosed', false);
            }
          },
        ),
      );
      this.subscriptions.add(
        combineLatest([
          referralTokenCollection.valueChanges(),
          this.auth.isLoggedIn(),
        ]).subscribe(([referralTokens, isLoggedIn]) => {
          if (referralTokens.length > 0 && !isLoggedIn) {
            this.cookies.put('referral_signup', referralTokens[0].id);
          }
        }),
      );
    } else {
      this.showGiveGetFooter$ = of(false);
    }
  }

  closeFooter(): void {
    this.showGiveGetFooter$ = of(false);
    this.localStorage.set('giveGetFooterClosed', true);
  }

  openLoginSignupModal(): void {
    this.showGiveGetFooter$ = of(false);
    this.modalService
      .open('LoginSignupModal', {
        closeable: true,
        edgeToEdge: true,
        mobileFullscreen: true,
        inputs: {
          form: LoginOrSignup.SIGNUP,
          enableLoginSignupSwitch: false,
        },
        size: ModalSize.XSMALL,
      })
      .afterClosed()
      .then(userId => {
        if (userId) {
          this.closeFooter();
          this.router.navigate([`/new-client`]);
        } else {
          this.showGiveGetFooter$ = of(true);
        }
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
